import { clearPortalSentryUser } from '@admin-panel/observability';
import { LOCAL_GATEWAY_AUTH_BYPASS_ENABLED, PORTAL_NAME } from '@admin-panel/config/env';

type HttpErrorLike = {
  response?: {
    status?: unknown;
  };
  status?: unknown;
  statusCode?: unknown;
};

let hasHandledUnauthorizedSession = false;

const toStatusCode = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return undefined;
};

export const getHttpStatusCode = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const maybeError = error as HttpErrorLike;

  return (
    toStatusCode(maybeError.response?.status) ??
    toStatusCode(maybeError.statusCode) ??
    toStatusCode(maybeError.status)
  );
};

export const isUnauthorizedHttpError = (error: unknown) => getHttpStatusCode(error) === 401;

export const resetUnauthorizedSessionHandling = () => {
  hasHandledUnauthorizedSession = false;
};

/**
 * Facts about this device, not the signed-in person, so they survive logout: the device identity
 * the next login request needs, Hub's confirmed alert sound test (a shared phone stays set up),
 * Hub's Local/Stage alert-setup preview mode, Hub's installation id and last setup report
 * (adoption tracking counts devices, not logins), and which operators chose "Ring for all my
 * facilities" on this device.
 */
export const DEVICE_STORAGE_KEYS = [
  'fingerprint',
  'hub-alert-setup-done',
  'hub-alert-setup-mode',
  'hub-alerts-ring-all',
  'hub-install-id',
  'hub-setup-report',
] as const;

export const clearClientStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  clearPortalSentryUser();
  const kept = DEVICE_STORAGE_KEYS.map(key => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  kept.forEach(([key, value]) => {
    if (value) {
      localStorage.setItem(key, value);
    }
  });
  sessionStorage.clear();
};

// Foundation's STN approval deep link (sent in approval emails), rebuilt from validated parts.
const stnApprovalReturn = (returnTo?: string, search?: string) => {
  if (PORTAL_NAME !== 'admin' || returnTo !== '/stocktransfer' || !search) {
    return undefined;
  }
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (params.get('stockTransferTab') !== 'approval' || !id || !/^[1-9]\d*$/.test(id)) {
    return undefined;
  }
  return `/stocktransfer?stockTransferTab=approval&id=${id}${params.get('showTrial') === '1' ? '&showTrial=1' : ''}`;
};

const RECEIVE_FRAGMENT = /^#w=((?:PACKAGE|RETURN):[1-9]\d{0,15})(?:&c=(\d{6}))?$/;
/** This tab's sessionStorage key for a receive link's code carried through login. */
export const RECEIVE_CODE_KEY = 'lk-receive-code';
/** A code carried longer than this is dropped: the rider shows a fresh one anyway. */
const RECEIVE_CODE_TTL_MS = 30 * 60_000;

/**
 * Hub and Nexus receive links (`/receive#w=TYPE:id&c=code`, the rider's handover QR) keep their
 * parcel through login: the destination is rebuilt from the validated fragment only, WITHOUT the
 * code. The login URL's query reaches servers and their logs; the code must not.
 */
export const receiveDestination = (pathname?: string, hash?: string) => {
  if (!['warehouse', 'vendor'].includes(PORTAL_NAME) || pathname !== '/receive' || !hash) {
    return undefined;
  }
  const match = RECEIVE_FRAGMENT.exec(hash);
  return match ? `/receive#w=${match[1]}` : undefined;
};

/**
 * The code-free login destination for a receive link, with its code kept in this tab's
 * sessionStorage (written after any session clear) for ReceiveLinkPage to read once after login.
 */
export const carryReceiveLink = (pathname?: string, hash?: string) => {
  const destination = receiveDestination(pathname, hash);
  const match = destination ? RECEIVE_FRAGMENT.exec(hash!) : null;
  if (match?.[2] && typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(
        RECEIVE_CODE_KEY,
        JSON.stringify({ w: match[1], c: match[2], at: Date.now() }),
      );
    } catch {
      // Storage blocked: the parcel still opens; the receiver types the code.
    }
  }
  return destination;
};

/** The carried code for exactly this parcel, if still fresh. Does not remove it. */
export const peekReceiveCode = (work: { workType: string; workId: number }) => {
  try {
    const carried = JSON.parse(sessionStorage.getItem(RECEIVE_CODE_KEY) || 'null') as {
      w?: unknown;
      c?: unknown;
      at?: unknown;
    } | null;
    const age = Date.now() - Number(carried?.at);
    return carried?.w === `${work.workType}:${work.workId}` &&
      typeof carried.c === 'string' &&
      /^\d{6}$/.test(carried.c) &&
      age >= 0 &&
      age <= RECEIVE_CODE_TTL_MS
      ? carried.c
      : undefined;
  } catch {
    return undefined;
  }
};

export const clearReceiveCode = () => {
  try {
    sessionStorage.removeItem(RECEIVE_CODE_KEY);
  } catch {
    // Nothing carried.
  }
};

/** Reads the carried code once: any carried code is removed, matching or not. */
export const takeReceiveCode = (work: { workType: string; workId: number }) => {
  const code = peekReceiveCode(work);
  clearReceiveCode();
  return code;
};

export const redirectToLogin = (returnTo?: string, search?: string, hash?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.location.pathname !== '/login') {
    // Only expired Hub package views, Hub/Nexus receive links and Foundation STN approval links keep
    // a destination. Explicit logout passes no path; other redirect targets keep their existing behavior.
    const destination =
      PORTAL_NAME === 'warehouse' && returnTo && /^\/packages\/show\/[1-9]\d*$/.test(returnTo)
        ? returnTo
        : (carryReceiveLink(returnTo, hash) ?? stnApprovalReturn(returnTo, search));
    window.location.replace(`/login${destination ? `?to=${encodeURIComponent(destination)}` : ''}`);
  }
};

export const handleUnauthorizedSessionExpiry = () => {
  if (LOCAL_GATEWAY_AUTH_BYPASS_ENABLED) {
    return;
  }

  if (hasHandledUnauthorizedSession) {
    return;
  }

  hasHandledUnauthorizedSession = true;
  const returnTo = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const search = typeof window !== 'undefined' ? window.location.search : undefined;
  const hash = typeof window !== 'undefined' ? window.location.hash : undefined;
  clearClientStorage();
  redirectToLogin(returnTo, search, hash);
};
